import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Divider,
  TextField,
  Button,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';

interface ConversationMessage {
  id: string;
  sender_type: string;
  sender_id?: string;
  sender_name?: string;
  message: string;
  created_at: string;
}

interface SupportMessage {
  id: string;
  user_id: string;
  category: string;
  message: string;
  status: string;
  created_at: string;
  updated_at: string;
  admin_response?: {
    String: string;
    Valid: boolean;
  };
  responded_at?: {
    String: string;
    Valid: boolean;
  };
  conversation?: ConversationMessage[];
  user_name?: string; // Available in admin context
}

interface SupportChatModalProps {
  open: boolean;
  message: SupportMessage | null;
  onClose: () => void;
  onMessageSent?: () => void;
  isAdmin?: boolean;
  onSendReply?: (messageId: string, reply: string) => void;
}

const SupportChatModal: React.FC<SupportChatModalProps> = ({
  open,
  message,
  onClose,
  onMessageSent,
  isAdmin = false,
  onSendReply,
}) => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return language === 'ua' ? 'Нове' : 'New';
      case 'in_progress':
        return language === 'ua' ? 'В роботі' : 'In Progress';
      case 'resolved':
        return language === 'ua' ? 'Вирішене' : 'Resolved';
      case 'closed':
        return language === 'ua' ? 'Закрите' : 'Closed';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'ua' ? 'uk-UA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !message) return;

    setIsSending(true);
    try {
      if (isAdmin && onSendReply) {
        // Admin sending reply
        onSendReply(message.id, newMessage.trim());
      } else {
        // Client sending follow-up
        const response = await fetch(`/api/v1/support/messages/${message.id}/followup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            message: newMessage.trim(),
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send message');
        }

        addNotification(
          language === 'ua' ? 'Повідомлення надіслано!' : 'Message sent!',
          'success'
        );

        // Refresh the message data by calling the parent's refresh function
        onMessageSent?.();
      }

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      addNotification(
        language === 'ua' ? 'Помилка при надсиланні повідомлення' : 'Error sending message',
        'error'
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setNewMessage('');
    onClose();
  };

  // Auto-scroll to bottom when messages change
  // We need to calculate allMessages here for the dependency
  const allMessages: Array<{
    id: string;
    message: string;
    sender_type: 'client' | 'admin';
    sender_name?: string;
    created_at: string;
    isOriginal?: boolean;
  }> = [];

  if (message) {
    // Add original client message
    allMessages.push({
      id: `original-${message.id}`,
      message: message.message,
      sender_type: 'client',
      sender_name: isAdmin ? message.user_name : (language === 'ua' ? 'Ви' : 'You'),
      created_at: message.created_at,
      isOriginal: true,
    });

    // Add admin responses (legacy format) - only if no conversation exists
    if (message.admin_response?.Valid && (!message.conversation || message.conversation.length === 0)) {
      message.admin_response.String.split('\n\n').forEach((response, index) => {
        const timestampMatch = response.match(/^\[([^\]]+)\]\s*(.+)$/);
        if (timestampMatch) {
          const [, timestamp, messageText] = timestampMatch;
          // Parse timestamp based on environment (local dev vs production)
          const dateParts = timestamp.split(/[\.\s]+/);
          const day = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-indexed
          const year = parseInt(dateParts[2]);
          const timeParts = dateParts[3].split(':');
          const hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);
          const seconds = parseInt(timeParts[2] || '0');

          // In local development, backend runs with local timezone (Europe/Kiev)
          // In production, backend runs in UTC
          const isLocalDev = window.location.hostname === 'localhost' ||
                            window.location.hostname === '127.0.0.1' ||
                            window.location.hostname.includes('local');

          let parsedDate: Date;
          if (isLocalDev) {
            // Local development: parse as local time (backend formatted with local time)
            parsedDate = new Date(year, month, day, hours, minutes, seconds);
          } else {
            // Production: parse as UTC (backend stores as UTC)
            parsedDate = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
          }
          allMessages.push({
            id: `admin-legacy-${index}`,
            message: messageText,
            sender_type: 'admin',
            sender_name: language === 'ua' ? 'Адміністратор' : 'Admin',
            created_at: parsedDate.toISOString(),
          });
        }
      });
    }

    // Add conversation messages
    if (message.conversation) {
      message.conversation.forEach((msg) => {
        allMessages.push({
          id: msg.id,
          message: msg.message,
          sender_type: msg.sender_type as 'client' | 'admin',
          sender_name: msg.sender_name || (msg.sender_type === 'admin' ? (language === 'ua' ? 'Адміністратор' : 'Admin') : (isAdmin ? message?.user_name : (language === 'ua' ? 'Ви' : 'You'))),
          created_at: msg.created_at,
        });
      });
    }

    // Sort by creation date
    allMessages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [allMessages]);

  if (!message) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          maxHeight: '600px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                {isAdmin
                  ? (language === 'ua' ? 'Чат з клієнтом' : 'Chat with Client')
                  : (language === 'ua' ? 'Чат з підтримкою' : 'Support Chat')
                }
              </Typography>
              <Chip
                label={getStatusText(message.status)}
                color={
                  message.status === 'resolved' ? 'success' :
                  message.status === 'in_progress' ? 'warning' :
                  message.status === 'new' ? 'info' : 'default'
                }
                size="small"
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {message.category} - {formatDate(message.created_at)}
              {!isAdmin && (
                <span> • {message.user_name}</span>
              )}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 1,
              minHeight: 300,
            }}
          >
            {allMessages.map((msg) => {
              // Determine if this message is from "me" (current user)
              const isFromMe = isAdmin ? msg.sender_type === 'admin' : msg.sender_type === 'client';
              const justifyContent = isFromMe ? 'flex-end' : 'flex-start';
              const alignItems = isFromMe ? 'flex-end' : 'flex-start';
              const bgcolor = isFromMe ? 'primary.main' : 'grey.100';
              const color = isFromMe ? 'primary.contrastText' : 'text.primary';

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent,
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '70%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor,
                        color,
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 1,
                      }}
                    >
                      {/* Sender name */}
                      <Typography variant="caption" sx={{ opacity: 0.8, mb: 0.5, display: 'block' }}>
                        {msg.sender_name || (msg.sender_type === 'admin' ? (language === 'ua' ? 'Адміністратор' : 'Admin') : (isAdmin ? message?.user_name : (language === 'ua' ? 'Ви' : 'You')))}
                      </Typography>
                      <Typography variant="body2">{msg.message}</Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5, px: 1 }}
                    >
                      {formatDate(msg.created_at)}
                      {msg.isOriginal && (
                        <Chip
                          label={language === 'ua' ? 'Оригінал' : 'Original'}
                          size="small"
                          sx={{ ml: 1, fontSize: '0.7rem', height: '16px' }}
                        />
                      )}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
            {/* Invisible element to scroll to */}
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input */}
          <Divider />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={language === 'ua' ? 'Введіть ваше повідомлення...' : 'Enter your message...'}
              variant="outlined"
              size="small"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={isSending || !newMessage.trim()}
              sx={{ minWidth: 50, height: 40 }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SupportChatModal;
